import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/**
 * Searches the HUD PHA directory (`public_housing_authorities`) for the case
 * wizard's PHA picker.
 *
 *  - Empty query: area suggestions filtered to the property's `state`, so the
 *    right local PHA is usually one tap away when the picker opens.
 *  - Typed query: ILIKE on `pha_name` across ALL states — a manual search must
 *    not be trapped in the property's state (voucher portability, regional/
 *    state authorities, or a wrong property state would otherwise hide it).
 *  - Distance sort: when property coordinates are given, results are ordered by
 *    great-circle distance — so nearby PHAs still bubble up even on a nationwide
 *    search. It is a tie-breaker, NOT a jurisdiction test.
 *
 * The table is static public reference data absent from the generated Supabase
 * types, so it is read through a narrowly-typed view of the client.
 */

export interface Pha {
  participant_code: string;
  pha_name: string;
  pha_phone: string | null;
  pha_email: string | null;
  exec_director_phone: string | null;
  exec_director_email: string | null;
  city: string | null;
  state: string | null;
  county_name: string | null;
  latitude: number | null;
  longitude: number | null;
}

const PHA_COLUMNS =
  "participant_code, pha_name, pha_phone, pha_email, exec_director_phone, exec_director_email, city, state, county_name, latitude, longitude";

const MAX_RESULTS = 25;

interface RefFilter {
  ilike: (col: string, pattern: string) => RefFilter;
  eq: (col: string, val: unknown) => RefFilter;
  order: (col: string) => RefFilter;
  limit: (n: number) => Promise<{ data: Pha[] | null; error: unknown }>;
}
const refDb = supabase as unknown as {
  from: (t: string) => { select: (cols: string) => RefFilter };
};

function haversineMiles(aLat: number, aLng: number, bLat: number, bLng: number): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const lat1 = toRad(aLat);
  const lat2 = toRad(bLat);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 3958.8 * 2 * Math.asin(Math.min(1, Math.sqrt(h)));
}

export function usePhaSearch({ query, state, lat, lng, enabled = true }: {
  query: string;
  state?: string;
  lat?: number;
  lng?: number;
  enabled?: boolean;
}) {
  const trimmed = query.trim();

  const phasQuery = useQuery({
    queryKey: ["pha-search", state ?? "", trimmed],
    enabled,
    queryFn: async () => {
      let q = refDb.from("public_housing_authorities").select(PHA_COLUMNS);
      // State pre-filter only narrows the empty-query suggestions; once the user
      // types a name, search every state.
      if (trimmed) q = q.ilike("pha_name", `%${trimmed}%`);
      else if (state) q = q.eq("state", state);
      const { data, error } = await q.order("pha_name").limit(MAX_RESULTS);
      if (error) throw error;
      return data ?? [];
    },
  });

  const phas = (phasQuery.data ?? []).slice();
  if (lat !== undefined && lng !== undefined) {
    phas.sort((a, b) => {
      const da = a.latitude != null && a.longitude != null ? haversineMiles(lat, lng, a.latitude, a.longitude) : Infinity;
      const db = b.latitude != null && b.longitude != null ? haversineMiles(lat, lng, b.latitude, b.longitude) : Infinity;
      return da - db;
    });
  }

  return { phas, isLoading: phasQuery.isLoading };
}
